import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import "../CSS/PaymentMultiple.css";
import {
  Box,
  Typography,
  TextField,
  Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const PaymentMultiple = () => {

  const currentMonthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const currentMonthEnd = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);


  const [fromDate, setFromDate] = useState(currentMonthStart);
  const [toDate, setToDate] = useState(currentMonthEnd);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [paymentData, setPaymentData] = useState([]);
  const chartRef = React.useRef();
  const pieChartRef = React.useRef();
  const tooltipRef = useRef();
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  const [totalPaid, setTotalPaid] = useState(0);
  const [totalUnpaid, setTotalUnpaid] = useState(0);


  const [totalDaysAmount1, setTotalDaysAmount] = useState("");
  const [totalUnpaidAmount, setTotalUnpaidAmount] = useState("");

  useEffect(() => {
    const fetchTotalPaymentData = async () => {
      try {
        const response = await axios.get("https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getTotalAmount");
        const fetchedData = response.data;
  
        if (!fetchedData || !fetchedData.paymentsList) {
          return setTotalDaysAmount(0);
        }
  
        // Summing up the totalAmount across all users
        const sumOfTotalAmounts = fetchedData.paymentsList.reduce((sum, user) => {
          const amounts = user.totalAmount || [];
          const userSum = amounts.reduce((userTotal, value) => userTotal + Number(value), 0);
          return sum + userSum;
        }, 0);
  
        setTotalDaysAmount(sumOfTotalAmounts);
      } catch (error) {
        console.error("Error fetching payment data:", error);
      }
    };
  
    fetchTotalPaymentData();
  }, []);

  useEffect(() => {
    const fetchUnpaidPaymentsData = async () => {
      try {
        const response = await axios.get("https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getTotalUnpaidAmount");
        const paymentsList = response.data;
  
      
        const unpaidSum = paymentsList.reduce((sum, payment) => sum + payment.remainingAmount, 0);
  
        setTotalUnpaidAmount(unpaidSum);
      } catch (error) {
        console.error("Error fetching payment data:", error);
      }
    };
  
    fetchUnpaidPaymentsData();
  }, []);

  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        const response = await axios.get("https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getPayments");
        const fetchedData = response.data;
        setPaymentData(fetchedData);
      } catch (error) {
        console.error("Error fetching payment data:", error);
      }
    };
    fetchPaymentData();
  }, []);

  // Filter data based on selected date range
  const filteredData = paymentData
  .filter((entry) => (selectedCategory ? entry.category === selectedCategory : true)) // Filter by category
  .flatMap((entry) =>
    entry.date
      .filter((d) => {
        const entryDate = new Date(d);
        return entryDate >= fromDate && entryDate <= toDate;
      })
      .map((filteredDate) => ({
        ...entry,
        date: filteredDate,
      }))
  );

  

  useEffect(() => {
    const paidSum = filteredData.reduce((sum, entry) => {
      return sum + (entry.paidAmount ? parseFloat(entry.paidAmount) : 0);
    }, 0);
  
    const unpaidSum = filteredData.reduce((sum, entry) => {
      return sum + (entry.remainingAmount ? parseFloat(entry.remainingAmount) : 0);
    }, 0);
  
    setTotalPaid(paidSum);
    setTotalUnpaid(unpaidSum);
  }, [filteredData]);
  
  

  const aggregatedData = d3.rollups(
    filteredData,
    (entries) => {
      const statusCount = { Paid: 0, Unpaid: 0 }; 
      entries.forEach((entry) => {
        if (parseFloat(entry.remainingAmount) === 0) statusCount.Paid += 1;
        if (parseFloat(entry.remainingAmount) > 0) statusCount.Unpaid += 1;

      });
      return statusCount;
    },
    (d) => d.date
  );


  const chartData = aggregatedData.map(([date, statusCounts]) => ({
    date,
    Paid: statusCounts.Paid || 0,
    Unpaid: statusCounts.Unpaid || 0,
  }));

  const totalStatus = {
    Paid: d3.sum(chartData, (d) => d.Paid),
    Unpaid: d3.sum(chartData, (d) => d.Unpaid),
  };

  const totalDaysAmount = d3.sum(filteredData, (d) =>
    parseFloat(d.totalAmount || 0)
  );

  const totalPages = Math.ceil(chartData.length / rowsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const paginatedData = chartData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );


  useEffect(() => {
    if (!chartRef.current) return;
  
    const svg = d3.select(chartRef.current);
    const margin = { top: 20, right: 50, bottom: 40, left: 50 };
    const width = 600 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;
  
    svg.selectAll("*").remove();
  
    // Utility function to group and aggregate data by date
    const groupByDate = (data, fields) => {
      return data.reduce((acc, curr) => {
        const existing = acc.find((item) => item.date === curr.date);
        if (existing) {
          fields.forEach((field) => {
            existing[field] += parseFloat(curr[field] || 0);
          });
        } else {
          const newItem = { date: curr.date };
          fields.forEach((field) => {
            newItem[field] = parseFloat(curr[field] || 0);
          });
          acc.push(newItem);
        }
        return acc;
      }, []);
    };
  
    const aggregatedChartData = groupByDate(chartData, ["Paid", "Unpaid"]);
    const aggregatedFilteredData = groupByDate(filteredData, ["totalAmount", "paidAmount"]);
  
    const x = d3
      .scaleBand()
      .domain(aggregatedChartData.map((d) => d.date))
      .range([0, width])
      .padding(0.2);
  
    const yBar = d3
      .scaleLinear()
      .domain([0, d3.max(aggregatedChartData, (d) => d.Paid + d.Unpaid)])
      .nice()
      .range([height, 0]);
  
    const yLine = d3
      .scaleLinear()
      .domain([
        0,
        d3.max(aggregatedFilteredData, (d, i) => {
          const cumulativeTotal = aggregatedFilteredData
            .slice(0, i + 1)
            .reduce((sum, curr) => sum + curr.totalAmount, 0);
          const cumulativePaid = aggregatedFilteredData
            .slice(0, i + 1)
            .reduce((sum, curr) => sum + curr.paidAmount, 0);
          return Math.max(cumulativeTotal, cumulativePaid);
        }),
      ])
      .nice()
      .range([height, 0]);
  
    const color = d3.scaleOrdinal().domain(["Paid", "Unpaid"]).range(["#008000", "#f44336"]);
  
    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);
  
    // Tooltip container
    const tooltip = d3
      .select("body")
      .append("div")
      .style("position", "absolute")
      .style("background", "rgba(0, 0, 0, 0.7)")
      .style("color", "white")
      .style("padding", "8px")
      .style("border-radius", "4px")
      .style("visibility", "hidden")
      .style("pointer-events", "none");
  
    // X-axis
    g.append("g").attr("transform", `translate(0,${height})`).call(d3.axisBottom(x));
  
    // Y-axis for bars
    g.append("g").call(d3.axisLeft(yBar));
  
    // Y-axis for line chart
    g.append("g").attr("transform", `translate(${width},0)`).call(d3.axisRight(yLine));
  
    // Bar chart
    const bars = g
      .selectAll("g.bar-group")
      .data(aggregatedChartData)
      .enter()
      .append("g")
      .attr("class", "bar-group")
      .attr("transform", (d) => `translate(${x(d.date)},0)`);
  
    bars
      .selectAll("rect")
      .data((d) => [
        { status: "Paid", value: d.Paid, date: d.date },
        { status: "Unpaid", value: d.Unpaid, date: d.date },
      ])
      .enter()
      .append("rect")
      .attr("x", (d, i) => (x.bandwidth() / 2) * i)
      .attr("y", yBar(0))
      .attr("width", x.bandwidth() / 2)
      .attr("height", 0)
      .attr("fill", (d) => color(d.status))
      .on("mouseover", function (event, d) {
        d3.select(this).attr("opacity", 0.7);
        tooltip.style("visibility", "visible").html(
          `<strong>Status:</strong> ${d.status}<br/>
           <strong>Date:</strong> ${d.date}<br/>
           <strong>Value:</strong> ${d.value}`
        );
      })
      .on("mousemove", function (event) {
        tooltip
          .style("left", `${event.pageX + 10}px`)
          .style("top", `${event.pageY - 30}px`);
      })
      .on("mouseout", function () {
        d3.select(this).attr("opacity", 1);
        tooltip.style("visibility", "hidden");
      })
      .transition()
      .duration(2000)
      .attr("y", (d) => yBar(d.value))
      .attr("height", (d) => height - yBar(d.value));
  
    // Compute cumulative values for TotalAmount and PaidAmount
    const aggregatedData = aggregatedFilteredData.map((item, index) => {
      const cumulativeTotalAmount = aggregatedFilteredData
        .slice(0, index + 1)
        .reduce((sum, d) => sum + d.totalAmount, 0);
      const cumulativePaidAmount = aggregatedFilteredData
        .slice(0, index + 1)
        .reduce((sum, d) => sum + d.paidAmount, 0);
      return {
        ...item,
        cumulativeTotalAmount,
        cumulativePaidAmount,
      };
    });
  
    // Line chart for cumulative TotalAmount
    const totalLine = d3
      .line()
      .x((d) => x(d.date) + x.bandwidth() / 2)
      .y((d) => yLine(d.cumulativeTotalAmount));
  
    g.append("path")
      .datum(aggregatedData)
      .attr("fill", "none")
      .attr("stroke", "red")
      .attr("stroke-width", 2)
      .attr("d", totalLine);
  
    // Line chart for cumulative PaidAmount
    const paidLine = d3
      .line()
      .x((d) => x(d.date) + x.bandwidth() / 2)
      .y((d) => yLine(d.cumulativePaidAmount));
  
    g.append("path")
      .datum(aggregatedData)
      .attr("fill", "none")
      .attr("stroke", "green")
      .attr("stroke-width", 2)
      .attr("d", paidLine);
  
    // Add line points
    g.selectAll(".line-point")
  .data(aggregatedData)
  .enter()
  .append("circle")
  .attr("cx", (d) => x(d.date) + x.bandwidth() / 2)
  .attr("cy", (d) => yLine(d.cumulativeTotalAmount))
  .attr("r", 5)
  .attr("fill", "red")
  .on("mouseover", function (event, d) {
    d3.select(this).attr("r", 7); // Highlight the point
    tooltip
      .style("visibility", "visible")
      .html(
        `<strong>Total Amount:</strong> ${d.cumulativeTotalAmount}<br/>
         <strong>Date:</strong> ${d.date}`
      );
  })
  .on("mousemove", function (event) {
    tooltip
      .style("left", `${event.pageX + 10}px`)
      .style("top", `${event.pageY - 30}px`);
  })
  .on("mouseout", function () {
    d3.select(this).attr("r", 5); // Reset point size
    tooltip.style("visibility", "hidden");
  });
  
  g.selectAll(".line-point-paid")
  .data(aggregatedData)
  .enter()
  .append("circle")
  .attr("cx", (d) => x(d.date) + x.bandwidth() / 2)
  .attr("cy", (d) => yLine(d.cumulativePaidAmount))
  .attr("r", 5)
  .attr("fill", "green")
  .on("mouseover", function (event, d) {
    d3.select(this).attr("r", 7); // Highlight the point
    tooltip
      .style("visibility", "visible")
      .html(
        `<strong>Paid Amount:</strong> ${d.cumulativePaidAmount}<br/>
         <strong>Date:</strong> ${d.date}`
      );
  })
  .on("mousemove", function (event) {
    tooltip
      .style("left", `${event.pageX + 10}px`)
      .style("top", `${event.pageY - 30}px`);
  })
  .on("mouseout", function () {
    d3.select(this).attr("r", 5); // Reset point size
    tooltip.style("visibility", "hidden");
  });
  }, [chartData, filteredData]);
  
  
  useEffect(() => {
    if (!pieChartRef.current) return;
  
    const svg = d3.select(pieChartRef.current);
    const radius = 100;
    const margin = { top: 10, right: 10, bottom: 10, left: 10 };
    const width = 300 - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;
  
    svg.selectAll("*").remove();
  
    const arc = d3.arc().innerRadius(0).outerRadius(radius);
    const pie = d3.pie().value((d) => d.value);
  
    const color = d3.scaleOrdinal().range(["#008000", "#f44336"]);
  
    const g = svg
      .append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);
  
    const totalAmount = totalPaid + totalUnpaid;
  
    const pieData = pie([
      { status: "Paid", value: totalPaid },
      { status: "Unpaid", value: totalUnpaid },
    ]);
  
    const arcs = g
      .selectAll(".arc")
      .data(pieData)
      .enter()
      .append("g")
      .attr("class", "arc");
  
    arcs
      .append("path")
      .attr("d", arc)
      .attr("fill", (d) => color(d.data.status))
      .on("mouseover", function (event, d) {
        d3.select(this).attr("opacity", 0.7); // Change opacity on hover
        tooltipRef.current.style.visibility = "visible";
        tooltipRef.current.innerHTML = `
          <strong>Status:</strong> ${d.data.status}<br/>
          <strong>Count:</strong> ${d.data.value}<br/>
          <strong>Percentage:</strong> ${((d.data.value / totalAmount) * 100).toFixed(2)}%
        `;
        tooltipRef.current.style.left = `${event.pageX + 10}px`;
        tooltipRef.current.style.top = `${event.pageY - 30}px`;
      })
      .on("mouseout", function () {
        d3.select(this).attr("opacity", 1); 
        tooltipRef.current.style.visibility = "hidden";
      })
      .transition() // Add transition
      .duration(3000)
      .attrTween("d", function (d) {
          const interpolate = d3.interpolate({ startAngle: 0, endAngle: 0 }, d);
          return function (t) {
              return arc(interpolate(t));
          };
      });
  
    arcs
      .append("text")
      .attr("transform", (d) => `translate(${arc.centroid(d)})`)
      .attr("dy", ".35em")
      .attr("text-anchor", "middle")
      .attr("font-size", "12px")
      .text((d) => {
        const percentage = ((d.data.value / totalAmount) * 100).toFixed(2);
        return `${d.data.status} (${percentage}%)`; // Show status with percentage
      });
  }, [totalPaid, totalUnpaid]);
  
  const columns = [
    { field: "id", headerName: "S.No", width: 90 },
    { field: "date", headerName: "Date", width: 200 },
    { field: "paid", headerName: "Paid", width: 150 },
    { field: "Unpaid", headerName: "Unpaid", width: 150 },
];

const rows = chartData.map((entry, index) => ({
  id: index + 1,
  date: entry.date,
  paid: entry.Paid,
  Unpaid: entry.Unpaid,
}));
 

  return (
    <>
      <div className="Payment-dashboard">
      <h2 className="text-center mb-4 p-4">Payment Dashboard</h2>
      <div className="cards-container" style={{ display: "flex", gap: "20px", marginBottom: "20px", width: "80%" }}>
        <div className="card" style={{ flex: 1, padding: "20px", border: "1px solid #ccc", borderRadius: "10px",background: "#ffffff",textAlign: "center" }}>
            <h3>Total Amount</h3>
            <p>{"Rs "+totalDaysAmount1}</p>
        </div>
        <div className="card" style={{ flex: 1, padding: "20px", border: "1px solid #ccc", borderRadius: "10px",background: "#ffffff",textAlign: "center" }}>
            <h3>Total Unpaid Amount</h3>
            <p>{"Rs "+totalUnpaidAmount}</p>
          </div>
      </div>
        <div className="paymentdate" style={{ display: "flex", marginBottom: "20px" }}>
          <div>
            <label>From Date:</label>
            <DatePicker selected={fromDate} onChange={(date) => setFromDate(date)} />
          </div>
          <div>
            <label>To Date:</label>
            <DatePicker selected={toDate} onChange={(date) => setToDate(date)} />
          </div>
          <div>
            <label>Category:</label>
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
              <option value="">All</option>
              <option value="SingleDetails">SingleDetails</option>
              <option value="Membership">Membership</option>
            </select>
          </div>
        </div>

        <div className="cards-container" style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
          
          <div className="card paymentcard" style={{  padding: "20px", border: "1px solid #ccc", borderRadius: "10px",background: "#ffffff",textAlign: "center" }}>
            <h3>Total Paid Amount</h3>
            <p>{"Rs "+totalPaid}</p>
          </div>
          
        </div>

        <div className="chart-legend" style={{ display: "flex", alignItems: "end", marginTop: "20px", right: "0" }}>
                <div style={{ display: "flex", alignItems: "center", marginRight: "15px" }}>
                    <span
                        style={{
                            width: "15px",
                            height: "15px",
                            backgroundColor: "#008000",
                            display: "inline-block",
                            marginRight: "5px",
                            borderRadius: "3px",
                        }}
                    ></span>
                    <span>Paid</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                        style={{
                            width: "15px",
                            height: "15px",
                            backgroundColor: "#f44336",
                            display: "inline-block",
                            marginRight: "5px",
                            borderRadius: "3px",
                        }}
                    ></span>
                    <span>Unpaid</span>
                </div>
            </div>
        <h2 className="text-center mb-4">Daily Analysis</h2>  
      
        
        <div className="chart-container">
        <div
                ref={tooltipRef}
                style={{
                    position: "absolute",
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    padding: "8px",
                    borderRadius: "4px",
                    visibility: "hidden",
                    pointerEvents: "none",
                    zIndex: 10,
                }}
            ></div>
          <svg className="Paymentchartref" ref={chartRef} width="600" height="400"></svg>
        </div>
        <h2 className="text-center mb-4">Overall Analysis</h2>                  
        <div className="pie-chart-container">
          <svg ref={pieChartRef} width="300" height="300"></svg>
        </div>

       

        <h2 className="text-center mb-4">Daily Analysis</h2>  
        <Box sx={{ padding: 4 }}>
    

          <Paper sx={{ height: "auto", width: "45%", marginLeft: "30%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5, 10, 20]}
            disableSelectionOnClick
            autoHeight
            sortingOrder={['asc', 'desc']}
            
            
          />
          </Paper>
          </Box>
                </div>
    </>
  );
};

export default PaymentMultiple;
